import { Axios } from '@helpers/ApiService';
import { PdfType } from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';

export interface PdfDownloadOptions {
  endpoint: string;
  payload: Record<string, unknown>;
  filename?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Service for downloading PDF files from API endpoints
 * Provides reusable functionality for different types of PDF downloads
 */
export const PdfDownloadService = {
  /**
   * Downloads a PDF from the specified API endpoint
   * @param options - Configuration options for the PDF download
   */
  async downloadPdf(options: PdfDownloadOptions): Promise<void> {
    const { endpoint, payload, filename, onSuccess, onError } = options;

    try {
      const response = await Axios.post(endpoint, payload, {
        responseType: 'blob', // Important for binary data
        headers: {
          Accept: 'application/pdf',
        },
      });

      // Create blob URL for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = url;

      // Set filename - use provided filename or extract from response headers
      let downloadFilename = filename;
      if (!downloadFilename) {
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          downloadFilename = filenameMatch ? filenameMatch[1] : 'document.pdf';
        } else {
          downloadFilename = 'document.pdf';
        }
      }

      link.download = downloadFilename || 'document.pdf';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('PDF download failed:', error);

      // Call error callback if provided
      if (onError) {
        onError(
          error instanceof Error ? error : new Error('PDF download failed'),
        );
      } else {
        // Default error handling
        throw error;
      }
    }
  },

  /**
   * Downloads an order summary PDF
   * @param orderId - The ID of the order
   * @param filename - Optional custom filename
   * @param onSuccess - Success callback
   * @param onError - Error callback
   */
  async downloadOrderSummaryPdf(
    orderId: number,
    filename?: string,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ): Promise<void> {
    return this.downloadPdf({
      endpoint: ApiEndpoints.ORDER_GENERATE_PDF,
      payload: { id: orderId },
      filename: filename || `order-summary-${orderId}.pdf`,
      onSuccess,
      onError,
    });
  },

  /**
   * Downloads an invoice PDF from the server for a specific invoice ID.
   *
   * This function wraps around the `downloadPdf` utility to request and save an invoice
   * as a PDF file. It automatically constructs the download payload and handles success
   * or error callbacks.
   *
   * @async
   * @function downloadInvoicePdf
   * @param invoiceId - The ID of the invoice
   * @param filename - Optional custom filename
   * @param onSuccess - Success callback
   * @param onError - Error callback
   * @returns {Promise<void>} A Promise that resolves when the download process completes.
   */
  async downloadInvoicePdf(
    invoiceId: number,
    filename?: string,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ): Promise<void> {
    return this.downloadPdf({
      endpoint: ApiEndpoints.ORDER_INVOICE_VIEW,
      payload: { id: invoiceId, download: PdfType.DOWNLOAD }, // Send 1 for downloading PDF
      filename: filename || `invoice-${invoiceId}.pdf`,
      onSuccess,
      onError,
    });
  },
};
